/**
 * PlanSelectorField — button + filter based service-plan picker
 *
 * Replaces the old `<select>` dropdown for `service_plans` intake fields in
 * the New Deal wizard. Plans render as tappable cards; filter chips along the
 * top let you narrow by tier ("type") and surface popular plans first.
 *
 * Value contract is unchanged — the field stores the plan's `displayName`
 * string (or a free-text custom value), so every downstream consumer
 * (`extractServicePlansFromDeal`, `addServicePlansToQuote`, billing, etc.)
 * keeps working exactly as before.
 */

import { useEffect, useMemo, useState } from 'react';
import { Check, DollarSign, Loader2, Pencil, Star, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import type { ServicePlan } from '@/types/schema';
import { formatCurrency } from '@/utils/pricing';

interface Props {
  plans: ServicePlan[];
  /** Current selection — a plan `displayName` or a free-text custom value. */
  value: string;
  onChange: (value: string) => void;
  loading?: boolean;
  disabled?: boolean;
  /** Show monthly pricing on each card (mirrors the old `showPricing`). */
  showPricing?: boolean;
  /** Allow a free-text "Custom plan" entry (mirrors the old `allowCustom`). */
  allowCustom?: boolean;
}

const POPULAR_FILTER = '__popular__';
const ALL_FILTER = '__all__';

export function PlanSelectorField({
  plans,
  value,
  onChange,
  loading,
  disabled,
  showPricing,
  allowCustom = true,
}: Props) {
  const strVal = value ?? '';

  // Distinct plan tiers ("types") present in the data, kept in the order the
  // plans are already sorted (promoted → sortOrder). Drives the filter chips.
  const tiers = useMemo(() => {
    const seen: string[] = [];
    for (const p of plans) {
      const t = p.planTier?.trim();
      if (t && !seen.includes(t)) seen.push(t);
    }
    return seen;
  }, [plans]);

  const hasPopular = useMemo(() => plans.some((p) => p.isPromoted), [plans]);

  // Active filter — defaults to "All" so every plan is visible up front
  // (the hook already sorts promoted plans first and they carry a badge).
  const [filter, setFilter] = useState<string>(ALL_FILTER);

  // Plans come from a live onSnapshot subscription, so a tier (or the
  // "Popular" set) can vanish while the picker is open. If the active
  // filter no longer maps to any chip, fall back to "All" so we never
  // strand the user on an empty list with no highlighted chip.
  useEffect(() => {
    if (filter === ALL_FILTER) return;
    if (filter === POPULAR_FILTER) {
      if (!hasPopular) setFilter(ALL_FILTER);
      return;
    }
    if (!tiers.includes(filter)) setFilter(ALL_FILTER);
  }, [filter, tiers, hasPopular]);

  // A selection that isn't one of the known plans is a custom value.
  // `view` is the explicit user choice (null = not yet chosen); when unset
  // we fall back to the stored value's shape, so a hand-typed value shows
  // the custom input and a known plan shows the list — and it stays correct
  // after plans load async (the component returns the loading state first,
  // so `isCustom` is only consulted once `plans` is populated). Tracking an
  // explicit `view` lets "Pick from list instead" switch back to the list
  // WITHOUT wiping the typed value (review finding).
  const planNames = useMemo(() => plans.map((p) => p.displayName), [plans]);
  const isCustom = strVal !== '' && !planNames.includes(strVal);
  const [view, setView] = useState<'list' | 'custom' | null>(null);
  const showCustom = (view ?? (isCustom ? 'custom' : 'list')) === 'custom';

  const visiblePlans = useMemo(() => {
    if (filter === ALL_FILTER) return plans;
    if (filter === POPULAR_FILTER) return plans.filter((p) => p.isPromoted);
    return plans.filter((p) => (p.planTier?.trim() || '') === filter);
  }, [plans, filter]);

  // The currently-selected plan may be filtered out of view — always show it
  // so the selection never silently "disappears" behind a filter chip.
  const selectedPlan = plans.find((p) => p.displayName === strVal);
  const plansToRender = useMemo(() => {
    if (showCustom) return visiblePlans;
    if (selectedPlan && !visiblePlans.some((p) => p.id === selectedPlan.id)) {
      return [selectedPlan, ...visiblePlans];
    }
    return visiblePlans;
  }, [visiblePlans, selectedPlan, showCustom]);

  const showFilters = !showCustom && (hasPopular || tiers.length >= 2);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-3 text-xs text-white/40">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading plans…
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {/* Filter chips — "type" facets derived from the plan data */}
      {showFilters && (
        <div className="flex flex-wrap gap-1.5">
          <FilterChip
            label="All"
            active={filter === ALL_FILTER}
            onClick={() => setFilter(ALL_FILTER)}
            disabled={disabled}
          />
          {hasPopular && (
            <FilterChip
              label="Popular"
              icon={<Star className="w-3 h-3" />}
              active={filter === POPULAR_FILTER}
              onClick={() => setFilter(POPULAR_FILTER)}
              disabled={disabled}
            />
          )}
          {tiers.map((t) => (
            <FilterChip
              key={t}
              label={t}
              active={filter === t}
              onClick={() => setFilter(t)}
              disabled={disabled}
            />
          ))}
        </div>
      )}

      {/* Plan cards */}
      {!showCustom && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {plansToRender.length === 0 ? (
            <p className="text-xs text-white/30 py-2 col-span-full">
              No plans found{filter !== ALL_FILTER ? ' for this filter' : ''}.
              {allowCustom ? ' Add a custom plan below.' : ''}
            </p>
          ) : (
            plansToRender.map((plan) => {
              const selected = plan.displayName === strVal;
              return (
                <button
                  key={plan.id || plan.displayName}
                  type="button"
                  onClick={() => {
                    setView('list');
                    onChange(plan.displayName);
                  }}
                  disabled={disabled}
                  className={`relative text-left p-3 rounded-xl border transition-all ${
                    selected
                      ? 'bg-brand-secondary/15 border-brand-secondary/50 ring-1 ring-brand-secondary/30'
                      : 'bg-white/5 border-white/10 hover:border-white/20 hover:bg-white/[0.08]'
                  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`text-sm font-semibold truncate ${
                            selected ? 'text-brand-secondary' : 'text-white/80'
                          }`}
                        >
                          {plan.displayName}
                        </span>
                        {plan.isPromoted && (
                          <span className="shrink-0 inline-flex items-center gap-0.5 text-[9px] font-bold px-1 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-300 uppercase tracking-wide">
                            <Star className="w-2.5 h-2.5" /> Popular
                          </span>
                        )}
                      </div>
                      {(plan.downloadSpeed || plan.uploadSpeed || plan.dataAllowance) && (
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[10px] text-white/45">
                          {plan.downloadSpeed && plan.downloadSpeed !== 'N/A' && (
                            <span className="inline-flex items-center gap-0.5">
                              <ArrowDownToLine className="w-2.5 h-2.5" />
                              {plan.downloadSpeed}
                            </span>
                          )}
                          {plan.uploadSpeed && plan.uploadSpeed !== 'N/A' && (
                            <span className="inline-flex items-center gap-0.5">
                              <ArrowUpFromLine className="w-2.5 h-2.5" />
                              {plan.uploadSpeed}
                            </span>
                          )}
                          {plan.dataAllowance && plan.dataAllowance !== 'N/A' && (
                            <span>{plan.dataAllowance}</span>
                          )}
                        </div>
                      )}
                    </div>
                    {selected && (
                      <span className="shrink-0 w-4 h-4 rounded-full bg-brand-secondary flex items-center justify-center">
                        <Check className="w-3 h-3 text-brand-dark" />
                      </span>
                    )}
                  </div>
                  {showPricing && (
                    <div className="mt-2 flex items-baseline gap-1">
                      {/* TODO(saas-readiness): hardcoded '$' — should format
                          via Intl.NumberFormat with tenant.business.currency.
                          Carried over from the dropdown this replaced. */}
                      <span
                        className={`text-sm font-bold ${
                          selected ? 'text-emerald-600 dark:text-emerald-300' : 'text-emerald-400/80'
                        }`}
                      >
                        {formatCurrency(plan.sellPrice, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-[10px] text-white/40">/mo</span>
                      {plan.setupFee ? (
                        <span className="text-[10px] text-white/35 ml-1">
                          + {formatCurrency(plan.setupFee, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} setup
                        </span>
                      ) : null}
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}

      {/* Custom plan toggle + input */}
      {allowCustom && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => {
              if (showCustom) {
                // Switch back to the list WITHOUT clearing — browsing the
                // list shouldn't destroy a half-typed custom plan name.
                setView('list');
              } else {
                // Entering custom mode starts a fresh entry.
                setView('custom');
                onChange('');
              }
            }}
            disabled={disabled}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              showCustom
                ? 'bg-brand-secondary/15 text-brand-secondary border border-brand-secondary/40'
                : 'bg-white/5 text-white/50 border border-white/10 hover:border-white/20 hover:text-white/70'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Pencil className="w-3 h-3" />
            {showCustom ? 'Pick from list instead' : 'Custom plan'}
          </button>
          {showCustom && (
            <div className="space-y-1.5">
              <input
                type="text"
                value={strVal}
                onChange={(e) => onChange(e.target.value)}
                placeholder="e.g. NBN 50/20"
                disabled={disabled}
                autoFocus
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-white/30 focus:outline-none focus:border-brand-secondary/50 disabled:opacity-50"
              />
              <p className="text-[10px] text-white/35 inline-flex items-center gap-1">
                <DollarSign className="w-2.5 h-2.5" />
                Custom plans get added at $0 — set the price in the proposal builder.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FilterChip({
  label,
  icon,
  active,
  onClick,
  disabled,
}: {
  label: string;
  icon?: React.ReactNode;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
        active
          ? 'bg-brand-secondary/20 text-brand-secondary border border-brand-secondary/40'
          : 'bg-white/5 text-white/45 border border-white/10 hover:border-white/20 hover:text-white/70'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {icon}
      {label}
    </button>
  );
}
